import { api } from '@/shared/api/generated'
import { getAuthToken } from '@/shared/api/client'
import type { ListFilesResponse, ClientConfig } from '@/shared/api/generated'

let cachedConfig: ClientConfig | null = null

export const filesApi = {
  list: (path: string, groupID = 0, offset = 0, limit = 50): Promise<ListFilesResponse> => {
    const params: Record<string, string> = { path, offset: String(offset), limit: String(limit) }
    if (groupID) params['group_id'] = String(groupID)
    return api.listFiles(params)
  },

  getUploadUrl: async (req: { path: string; filename: string; size: number; groupID: number }): Promise<{ url: string; versionedName?: string }> => {
    const fullPath = req.path.replace(/\/$/, '') + '/' + req.filename

    if (cachedConfig?.proxy_mode) {
      const form = new FormData()
      form.append('path', fullPath)
      // Return a sentinel — the actual upload is done by proxyUpload
      return { url: '__proxy__', versionedName: undefined }
    }

    const resp = await api.getUploadUrl({ path: fullPath, content_type: '', group_id: 0 })
    let versionedName: string | undefined
    if (resp.final_path) {
      const idx = resp.final_path.lastIndexOf('/')
      versionedName = idx >= 0 ? resp.final_path.substring(idx + 1) : resp.final_path
    }
    return { url: resp.url, versionedName }
  },

  /** Upload file through server proxy. Returns versioned name if renamed. */
  proxyUpload: async (
    path: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<{ versionedName?: string }> => {
    const form = new FormData()
    form.append('file', file)
    form.append('path', path)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', '/api/files/upload-proxy')
      const token = getAuthToken()
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress((e.loaded / e.total) * 100)
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const resp = JSON.parse(xhr.responseText)
          let versionedName: string | undefined
          if (resp.final_path) {
            const idx = resp.final_path.lastIndexOf('/')
            versionedName = idx >= 0 ? resp.final_path.substring(idx + 1) : resp.final_path
          }
          resolve({ versionedName })
        } else {
          reject(new Error(xhr.responseText || `HTTP ${xhr.status}`))
        }
      }
      xhr.onerror = () => reject(new Error('Upload failed'))
      xhr.send(form)
    })
  },

  getDownloadUrl: async (req: { path: string; groupID: number }): Promise<string> => {
    if (cachedConfig?.proxy_mode) {
      return `/api/files/download-proxy?path=${encodeURIComponent(req.path)}`
    }
    const resp = await api.getDownloadUrl({ path: req.path, group_id: 0, content_type: '' })
    return resp.url
  },

  /** Get a URL suitable for inline preview (img src, iframe src, fetch). */
  getPreviewUrl: async (req: { path: string; groupID: number }): Promise<string> => {
    if (cachedConfig?.proxy_mode) {
      const token = (await import('@/shared/api/client')).getAuthToken()
      return `/api/files/preview-proxy?path=${encodeURIComponent(req.path)}&token=${encodeURIComponent(token || '')}`
    }
    const resp = await api.getDownloadUrl({ path: req.path, group_id: 0, content_type: '', inline: true })
    return resp.url
  },

  deleteFile: (path: string, _groupID: number): Promise<void> =>
    api.deleteFile({ path, group_id: 0, content_type: '' }).then(() => {}),

  createFolder: (path: string, _groupID: number): Promise<void> =>
    api.createFolder({ path, group_id: 0 }).then(() => {}),

  deleteFolder: (path: string, _groupID: number): Promise<void> =>
    api.deleteFolder({ path, group_id: 0 }).then(() => {}),

  rename: (oldPath: string, newPath: string, _groupID: number): Promise<void> =>
    api.renameFile({ old_path: oldPath, new_path: newPath, group_id: 0, confirm: false }).then(() => {}),

  move: (sourcePath: string, targetPath: string, _groupID: number): Promise<void> =>
    api.moveFile({ source_path: sourcePath, target_path: targetPath, group_id: 0, preserve_path: false }).then(() => {}),

  getConfig: async (): Promise<ClientConfig> => {
    const config = await api.getFileConfig()
    cachedConfig = config
    return config
  },

  isProxyMode: (): boolean => cachedConfig?.proxy_mode ?? false,
}
